import { build } from '@noths/polaris-product-listings-urls';

import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { selectHash, selectPathname } from 'src/redux/navigation/selectors';
import type { SortOption } from 'src/redux/productsQuery/types/SortOption';
import { extractBasePath } from 'src/utils/extractBasePath';
import { shopSurfaceBasePath } from 'src/utils/shopSurfaceBasePath';
import { buildListingsURL } from '../builders/buildListingsURL';
import { type FilterMutation, applyFilterMutation } from '../facets/applyFilterMutation';
import { facetsToSelections } from '../facets/facetsToSelections';
import { buildListingsQueryParams } from './buildListingsQueryParams';

export type DeriveListingsURLPartsOptions = {
  /**
   * The base path to build onto. Overrides the path derived from the Redux
   * surface/pathname — used where the same selections must be emitted onto a
   * different base, e.g. a Next route pattern (`href`) vs the displayed path (`as`).
   */
  basePath?: string;
  /** A declarative edit to the facet/filter selections before the URL is built. */
  filter?: FilterMutation;
  includeHash?: boolean;
  page?: number;
  sortOption?: SortOption | null;
};

export type ListingsURLParts = {
  hash: string;
  path: string;
  queryParams: URLSearchParams;
};

export const deriveListingsURLParts = (
  state: ReduxApplicationState,
  options: DeriveListingsURLPartsOptions = {},
): ListingsURLParts => {
  const { facets } = state.productsQuery;
  const taxonomyAliases = state.productsQuery.taxonomyAttributeAliases ?? [];
  const basePath =
    options.basePath ??
    shopSurfaceBasePath(state.productsQuery.surface) ??
    extractBasePath(selectPathname(state) ?? '/');

  const filters = applyFilterMutation(facetsToSelections(facets), options.filter, {
    facets,
    taxonomyAliases,
  });

  const path = build({
    basePath,
    filters,
    taxonomyAliases,
  });

  return {
    hash: options.includeHash === false ? '' : selectHash(state) ?? '',
    path,
    queryParams: buildListingsQueryParams(state, {
      page: options.page ?? state.productsQuery.page,
      sortOption: options.sortOption,
    }),
  };
};

export const buildListingsURLFromState = (
  state: ReduxApplicationState,
  options: DeriveListingsURLPartsOptions = {},
): string => {
  const { hash, path, queryParams } = deriveListingsURLParts(state, options);
  return buildListingsURL(path, queryParams, hash);
};
